import loadable from '@loadable/component';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Col } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { Link, useHistory } from 'react-router-dom';
import ym from 'react-yandex-metrika';
import styled from 'styled-components';

import {
  GQL_CREATE_DIALOG,
  GQL_GET_ARTWORK, GQL_GET_ME,
  GQL_GET_USER_PAYMENTS,
  GQL_TRY_BYE,
} from '../../graphql/queries';
import { statusGenerator } from '../../helpers/atworks';
import { ACTIVE, MAIN_GRAY } from '../../helpers/colors';
import {
  ARTWORK_SECTION, ARTWORKS_SECTION, imageUrlGenerate, STATUS_MODERATION, STATUS_PUBLISHED,
} from '../../helpers/main';
import { useTranslation } from '../../components/hooks/useTranslation';
import { Color, ContentContainer, Status } from '../../helpers/mainStyles';
import { isTariffPaid } from '../../helpers/payment';
import { isAdmin } from '../../helpers/users';
import { RU_DOMAIN } from '../../locales';

const Icon = loadable(() => import('../../components/Icon'));
const MetaTags = loadable(() => import('../../components/MetaTags'));
const BreadCrumbs = loadable(() => import('../../components/Breadcrumbs'));
const CarouselLightBox = loadable(() => import('../../components/CarouselLightBox'), { ssr: false });
const ActionButtons = loadable(() => import('../../components/ActionButtons'));
const ArtworkTabs = loadable(() => import('../../components/artworks/artworkTabs'));
const ArtworkInfo = loadable(() => import('../../components/artworks/artworkInfo'));
const ArtworkInfoMore = loadable(() => import('../../components/artworks/artworkInfoMore'));
const ShareButtons = loadable(() => import('../../components/ShareButtons'));
const CommentArea = loadable(() => import('../../components/account/comments/index'));
const ErrorScreen = loadable(() => import('../Error'));

const ArtworkView = ({ match }) => {
  let paid = false;

  const param = !_.get(match, 'params.id')
    ? { url: _.get(match, 'params.url') }
    : { _id: _.get(match, 'params.id') };

  const timeUnixNow = Math.floor(Date.now() / 1000);

  const history = useHistory();
  const alert = useAlert();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [me, setMe] = useState({});

  const [dialogProcess] = useMutation(GQL_CREATE_DIALOG);

  const [byeInfo] = useMutation(GQL_TRY_BYE);

  const { data: dataMe } = useQuery(GQL_GET_ME, { fetchPolicy: 'cache-only' });

  const { error, data, loading } = useQuery(GQL_GET_ARTWORK, {
    variables: param,
    fetchPolicy: 'cache-and-network',
  });

  const [loadPayments, { data: dataPayments }] = useLazyQuery(GQL_GET_USER_PAYMENTS, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    ym(
      'hit',
      `/artworks/${_.get(match, 'params.url') || _.get(match, 'params.id')}`,
      { params: { user: _.get(dataMe, 'me._id') } },
    );
  }, []);

  useEffect(() => {
    if (_.get(dataMe, 'me._id')) {
      setMe(_.get(dataMe, 'me'));
      loadPayments();
    }
  }, [dataMe]);

  const item = _.get(data, 'artwork', {});

  const errors = _.get(error, 'graphQLErrors', []);

  const payments = _.get(dataPayments, 'userPayments', []);

  useEffect(() => {
    isTariffPaid(payments, setIsPaid);
  }, [payments]);

  if (_.isEmpty(errors) && _.isEmpty(item) && !loading) {
    return <ErrorScreen errors={[]} />;
  }

  if (!_.isEmpty(errors)) {
    return <ErrorScreen errors={errors} />;
  }

  const messageProcess = async (model) => {
    if (!me._id) {
      alert.show(t('login_or_register', { ns: 'index' }), { type: 'warning' });
      history.push('/login');
      return false;
    }
    if (me._id === model.owner_id) {
      alert.show(t('you_owner', { ns: 'alerts' }), { type: 'warning' });
      return false;
    }
    try {
      await dialogProcess({
        variables: {
          companion_id: model.owner_id,
        },
        fetchPolicy: 'no-cache',
      }).then(async (response) => {
        if (_.get(response, 'data.createDialog._id')) {
          await byeInfo({
            variables: {
              user_id: model.owner_id,
              item_id: model._id,
              model: ARTWORK_SECTION,
              dialog_id: _.get(response, 'data.createDialog._id'),
            },
          });
          history.push({
            pathname: `/account/messages/${_.get(response, 'data.createDialog._id')}`,
            state: { item: model, pay_request: true },
          });
        }
      });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return false;
    }
  };

  if (loading || _.isEmpty(item)) {
    return (
      <div className="loader">
        <div className="bar" />
      </div>
    );
  }

  const images = _.get(item, 'images', null) && !_.isEmpty(item.images)
    ? item.images.slice().sort((img, img2) => img.order - img2.order)
    : [];

  const authorName = `Художник ${_.get(item, 'artist.name', 'неизвестеный')}`;

  const ownerName = `Владелец ${_.get(item, 'owner.name', 'неизвестеный')}`;

  const artworkName = `Произведение искусства ${_.get(item, 'name', 'Без названия')}`;

  const path = `${RU_DOMAIN}/${ARTWORKS_SECTION}/${item.url || item._id}`;

  const image = !_.isEmpty(images)
    ? `${RU_DOMAIN}/files/${ARTWORKS_SECTION}/${imageUrlGenerate(
      images[0],
      'items',
      'original',
    )}` : null;

  const title = `${artworkName}. ${authorName} - ${ownerName} | Артократия`;

  const description = `${authorName}, ${artworkName}. ${ownerName}. Социальная сеть для ценителей искусства - Артократия`;

  const shareTitle = `${
    _.get(item, 'artist.name', null)
      ? t('meta_artist_name', { ns: 'artwork', name: _.get(item, 'artist.name') })
      : t('no_artist', { ns: 'index' })
  }. ${_.get(item, 'name', null) ? _.get(item, 'name') : t('no_name', { ns: 'index' })}`;

  const shareDescription = ownerName;

  const breadcrumbs = [
    {
      name: item.is_market ? t('market', { ns: 'artwork' }) : t('catalog', { ns: 'artwork' }),
      url: item.is_market ? '/market-place' : '/artworks',
    },
  ];

  if (_.get(item, 'artist.name') && _.get(item, 'artist.status') === STATUS_PUBLISHED) {
    breadcrumbs.push({
      name: _.get(item, 'artist.name'),
      url: `/artists/${_.get(item, 'artist.url') || _.get(item, 'artist._id')}`,
    });
  }

  if (isPaid) {
    paid = true;
  } else if (_.get(item, 'expired', 0) > timeUnixNow) {
    paid = true;
  }

  const {
    message,
    color,
    infoColor,
    leftTime,
  } = statusGenerator({ item, t, isPaid: paid });

  return (
    <>
      <Artwork itemScope itemType="https://schema.org/VisualArtwork">
        {title && (
          <MetaTags
            title={title}
            description={description}
            published={_.get(item, 'created_at', null)}
            updated={_.get(item, 'updated_at', null)}
            image={image}
            path={path}
          />
        )}
        <ContentContainer>
          <div className="d-flex flex-column-reverse flex-lg-row">
            <ColStyled xl={{ span: 10, offset: 1 }} lg={{ span: 10, offset: 1 }} md={12} sm={12}>
              {_.get(me, '_id') && _.get(item, 'owner_id') && me._id === item.owner_id ? (
                <div className="d-flex justify-content-between align-items-center">
                  <BreadCrumbs padding="60px 0 20px 0" data={breadcrumbs} />
                  <ActionArea>
                    <div className="d-flex align-items-center">
                      <Status>
                        {t('name', { ns: 'status' })}
                        <Color color={color}>{message}</Color>
                        {leftTime && infoColor && (<InfoLeft color={infoColor}>{t('left', { ns: 'action', left: leftTime })}</InfoLeft>)}
                      </Status>
                    </div>
                    {_.get(item, 'status') !== STATUS_MODERATION && (
                      <Button
                        variant="outline-secondary"
                        className="d-flex ms-5"
                        onClick={() => history.push(`/account/artworks/${item._id}`)}
                      >
                        <Icon icon="edit" width={20} height={20} noHover colorStroke={ACTIVE} />
                      </Button>
                    )}
                  </ActionArea>
                </div>
              ) : (
                <BreadCrumbs padding="40px 0 20px 0" data={breadcrumbs} />
              )}
              <div className="bg-white p-3 p-md-5">
                <CarouselLightBox
                  item={{ images, ...item }}
                  folder="artworks"
                  color="#878787"
                  fsButton
                  downloadButton={
                    _.get(me, '_id', false) && (item.owner_id === me._id || isAdmin(me))
                  }
                />
                <HR />
                <H1 itemProp="name">{_.get(item, 'name') || t('no_name', { ns: 'index' })}</H1>
                <span
                  itemProp="creator"
                  itemScope
                  itemType="https://schema.org/Person"
                >
                  {_.get(item, 'artist.status') && item.artist.status === STATUS_PUBLISHED ? (
                    <Link
                      itemProp="sameAs"
                      className="text-dark"
                      to={`/artists/${_.get(item, 'artist.url') || _.get(item, 'artist._id')}`}
                    >
                      <H2 itemProp="name">{_.get(item, 'artist.name') || t('no_artist', { ns: 'index' })}</H2>
                    </Link>
                  ) : (
                    <H2 itemProp="name">{_.get(item, 'artist.name') || t('no_artist', { ns: 'index' })}</H2>
                  )}
                </span>
                {!_.isEmpty(item.categories) && (
                  <>
                    {t('category', { ns: 'artwork' })}
                    {': '}
                    {item.categories.map((category, i) => (
                      <React.Fragment key={category._id}>
                        {i ? ', ' : ''}
                        <Link itemProp="artMedium" className="text-dark" to={`/artworks?categories_id=${category._id}`}>
                          {category.name}
                        </Link>
                      </React.Fragment>
                    ))}
                  </>
                )}
                {!_.isEmpty(item) && (
                  <Info>
                    <ArtworkInfo
                      messageProcess={messageProcess}
                      links
                      count={100}
                      item={item}
                      preview={false}
                    />
                  </Info>
                )}
              </div>
              {_.get(item, 'description') && (
                <div className="bg-white mt-4 p-3 p-md-5">
                  <Description
                    itemProp="description"
                    className="content"
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </div>
              )}

              {_.get(me, '_id', false) && (item.owner_id === me._id || isAdmin(me)) && (
              <Private>
                <Name>
                  <div>
                    <Icon
                      icon="eye"
                      width={22}
                      height={22}
                      colorStroke={ACTIVE}
                      className="me-3"
                      noHover
                    />
                    {t('private_info', { ns: 'artwork' })}
                  </div>
                  <ButtonOpen type="button" onClick={() => setOpen(!open)}>
                    {open ? t('hide', { ns: 'index' }) : t('show', { ns: 'index' })}
                  </ButtonOpen>
                </Name>
                <MyInformation open={open}>
                  {!_.isEmpty(item) && (<ArtworkInfoMore item={item} />)}
                  {!_.isEmpty(item) && (<ArtworkTabs item={item} />)}
                </MyInformation>
              </Private>
              )}
              <Label>{t('conversation', { ns: 'artwork' })}</Label>

              <CommentsArea>
                <CommentArea
                  id={item._id}
                  me={me}
                  modelName={ARTWORK_SECTION}
                  expand={false}
                  bgColor="#F7F7F7"
                />
              </CommentsArea>

              {!isMobile && item.status === STATUS_PUBLISHED && (
                <ShareButtons
                  label={t('share_label', { ns: 'artwork' })}
                  name={shareTitle}
                  image={image}
                  url={path}
                  description={shareDescription}
                />
              )}
            </ColStyled>
            <Col xl={1} lg={1} md={12} sm={12} className="position-relative px-0">
              <ActionButtons
                top={220}
                item={item}
                section={ARTWORK_SECTION}
                refetchQueries={[
                  {
                    query: GQL_GET_ARTWORK,
                    variables: param,
                    fetchPolicy: 'network-only',
                  },
                ]}
                owner={item.owner}
              />
            </Col>
          </div>
        </ContentContainer>
      </Artwork>
      {isMobile && item.status === STATUS_PUBLISHED && (
        <ShareButtons
          label={t('share_label', { ns: 'artwork' })}
          name={shareTitle}
          image={image}
          url={path}
          description={shareDescription}
        />
      )}
    </>
  );
};

const InfoLeft = styled.div`
  color: ${({ color }) => color};
`;

const Description = styled.div`
  display: block;
`;

const ButtonOpen = styled.div`
  background-color: transparent;
  border: 0;
  padding: 0;
  font-size: 16px;
  color: ${ACTIVE};
  @media screen and (max-width: 500px) {
    font-size: 14px;
    margin-top: 20px;
  }
`;

const Private = styled.div`
  margin-top: 40px;
  background-color: ${MAIN_GRAY};
  border: 2px solid ${ACTIVE};
  padding: 15px;
  border-radius: 5px;
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${ACTIVE};
  @media screen and (max-width: 500px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    font-size: 14px;
    line-height: 1;
  }
`;

const MyInformation = styled.div`
  height: ${({ open }) => (open ? 'auto' : 0)};
  transition: height ease-in 300ms;
  overflow: hidden;
`;

const CommentsArea = styled.div`
  background: white;
  padding: 13px 50px 60px;
  @media screen and (max-width: 1024px) {
    padding: 5px 40px 40px;
  }
  @media screen and (max-width: 500px) {
    padding: 5px 16px 40px;
  }
`;

const Info = styled.div`
  color: #5e5c5c;
  line-height: 130%;
  @media screen and (max-width: 1024px) {
    font-size: 16px;
  }
  @media screen and (max-width: 500px) {
    font-size: 14px;
  }
`;

const ActionArea = styled.div`
  display: flex;
  align-items: center;
  @media screen and (max-width: 812px) {
    padding: 10px 0;
    width: 100%;
    justify-content: space-between;
    button {
      padding: 10px 12px;
    }
  }
`;

const Artwork = styled.div`
  padding-bottom: 60px;
  @media screen and (max-width: 812px) {
    overflow: hidden;
  }
  @media screen and (max-width: 500px) {
    padding-bottom: 30px;
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: bold;
  font-size: 38px;
  line-height: 120%;
  margin-bottom: 30px;
  padding: 48px 0 0;
  @media screen and (max-width: 1024px) {
    margin-bottom: 20px;
    padding: 10px 0 0;
    font-size: 32px;
  }
  @media screen and (max-width: 812px) {
    font-size: 28px;
  }
  @media screen and (max-width: 500px) {
    font-size: 20px;
    padding: 24px 0 0;
  }
`;

const ColStyled = styled(Col)`
  @media screen and (max-width: 812px) {
    padding: 0 !important;
  }
`;

const HR = styled.div`
  padding-top: 2.5rem;
  border-bottom: 1px solid #e9e9e9;
  margin-bottom: 2.5rem;
`;

const H1 = styled.h1`
  font-style: normal;
  font-weight: bold;
  font-size: 40px;
  @media screen and (max-width: 1024px) {
    font-size: 28px;
  }
  @media screen and (max-width: 500px) {
    font-size: 24px;
  }
`;

const H2 = styled.h2`
  font-size: 18px;
  line-height: 130%;
  @media screen and (max-width: 1024px) {
    font-size: 16px;
  }
  @media screen and (max-width: 500px) {
    font-size: 14px;
  }
`;

ArtworkView.propTypes = {
  match: PropTypes.object.isRequired,
};

export default ArtworkView;
